import React, { useState, useEffect } from 'react';
import {
  Tree,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Popconfirm,
  App,
  Tag,
  Divider,
  Input as AntdInput
} from 'antd';
import { PlusOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import knowledgeService from '../../services/knowledgeService';
import './KnowledgeManagement.css';

const levelLabels = {
  1: '考试名称',
  2: '学科名称',
  3: '考察方式',
  4: '课程名称',
  5: '章节名称',
  6: '知识点名称'
};

const levelColors = {
  1: 'magenta',
  2: 'red',
  3: 'volcano',
  4: 'orange',
  5: 'gold',
  6: 'lime'
};

// 展开层级配置
const expandLevels = {
  2: '学科名称',
  3: '考察方式',
  4: '课程名称',
  5: '章节名称',
  6: '知识点名称'
};

const KnowledgeManagement = () => {
  const [treeData, setTreeData] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [modalMode, setModalMode] = useState('add');
  const [modalTitle, setModalTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  const [editingNode, setEditingNode] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [formValues, setFormValues] = useState({
    name: '',
    level: 1,
    parent_id: null
  });
  const { message } = App.useApp();
  const [form] = Form.useForm();

  useEffect(() => {
    if (isModalVisible) {
      form.setFieldsValue(formValues);
    }
  }, [isModalVisible, formValues]);

  const fetchKnowledgeTree = async () => {
    try {
      setLoading(true);
      const response = await knowledgeService.getKnowledgeTree();
      if (response.code === 0) {
        setTreeData(response.data);
      } else {
        message.error(response.message || '获取知识树失败');
      }
    } catch (error) {
      message.error('获取知识树失败: ' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKnowledgeTree();
  }, []);

  const handleAdd = () => {
    setFormValues({
      name: '',
      level: 1,
      parent_id: null
    });
    setSelectedNode(null);
    setModalMode('add');
    setModalTitle('添加根节点');
    setIsModalVisible(true);
  };

  const handleAddChild = (node) => {
    if (node.level >= 6) {
      message.error('知识点名称层级不能添加子项');
      return;
    }
    setFormValues({
      name: '',
      level: node.level + 1,
      parent_id: node.id
    });
    setSelectedNode(node);
    setModalMode('add');
    setModalTitle(`添加${levelLabels[node.level + 1]}节点`);
    setIsModalVisible(true);
  };

  const handleEdit = (node) => {
    setSelectedNode(node);
    setModalMode('edit');
    setModalTitle(`编辑${levelLabels[node.level]}节点`);
    form.resetFields();
    form.setFieldsValue({
      name: node.name,
      level: node.level,
      parent_id: node.parent_id
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (node) => {
    try {
      const response = await knowledgeService.deleteKnowledge(node.id);
      if (response.code === 0) {
        message.success('删除成功');
        fetchKnowledgeTree();
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error) {
      message.error('删除失败: ' + (error.message || '未知错误'));
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (modalMode === 'add') {
        const response = await knowledgeService.createKnowledge(values);
        if (response.code === 0) {
          message.success('添加成功');
          setIsModalVisible(false);
          fetchKnowledgeTree();
        } else {
          message.error(response.message || '添加失败');
        }
      } else {
        const response = await knowledgeService.updateKnowledge(selectedNode.id, values);
        if (response.code === 0) {
          message.success('更新成功');
          setIsModalVisible(false);
          fetchKnowledgeTree();
        } else {
          message.error(response.message || '更新失败');
        }
      }
    } catch (error) {
      message.error('操作失败: ' + (error.message || '未知错误'));
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  // 搜索节点
  const searchNode = (value) => {
    setSearchValue(value);
    if (!value) {
      setExpandedKeys([]);
      return;
    }

    const getParentKeys = (node, targetId) => {
      if (node.id === targetId) {
        return [node.id];
      }
      if (node.children) {
        for (let child of node.children) {
          const result = getParentKeys(child, targetId);
          if (result) {
            return [node.id, ...result];
          }
        }
      }
      return null;
    };

    const findNode = (nodes, value) => {
      for (let node of nodes) {
        if (node.name.toLowerCase().includes(value.toLowerCase())) {
          return node.id;
        }
        if (node.children) {
          const found = findNode(node.children, value);
          if (found) return found;
        }
      }
      return null;
    };

    const targetId = findNode(treeData, value);
    if (targetId) {
      const parentKeys = getParentKeys({ children: treeData }, targetId);
      if (parentKeys) {
        setExpandedKeys(parentKeys);
      }
    }
  };

  const handleNodeClick = (node) => {
    setEditingNode(node);
    setEditingValue(node.name);
  };

  const handleNodeBlur = async () => {
    if (editingNode && editingValue !== editingNode.name) {
      try {
        const response = await knowledgeService.updateKnowledge(editingNode.id, {
          name: editingValue,
          level: editingNode.level,
          parent_id: editingNode.parent_id
        });
        if (response.code === 0) {
          message.success('更新成功');
          fetchKnowledgeTree();
        } else {
          message.error(response.message || '更新失败');
        }
      } catch (error) {
        message.error('更新失败: ' + (error.message || '未知错误'));
      }
    }
    setEditingNode(null);
  };

  const handleNodeChange = (e) => {
    setEditingValue(e.target.value);
  };

  const handleNodeKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleNodeBlur();
    } else if (e.key === 'Escape') {
      setEditingNode(null);
    }
  };

  // 生成章节序号
  const generateChapterNumber = (node) => {
    if (node.level !== 5) return ''; // 只为章节层级生成序号
    return `第${node.sort_order}章`;
  };

  // 渲染树节点
  const renderTreeNodes = (data) => {
    return data.map((item) => {
      const index = item.name.toLowerCase().indexOf(searchValue.toLowerCase());
      const beforeStr = item.name.substring(0, index);
      const matchStr = item.name.substring(index, index + searchValue.length);
      const afterStr = item.name.substring(index + searchValue.length);
      const chapterNumber = generateChapterNumber(item);
      const title =
        index > -1 ? (
          <span>
            {beforeStr}
            <span style={{ color: '#f50' }}>{matchStr}</span>
            {afterStr}
          </span>
        ) : (
          <span>{item.name}</span>
        );

      return {
        title: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {editingNode && editingNode.id === item.id ? (
              <Input
                value={editingValue}
                onChange={handleNodeChange}
                onBlur={handleNodeBlur}
                onKeyDown={handleNodeKeyDown}
                autoFocus
                style={{ width: '200px' }}
              />
            ) : (
              <span onClick={() => handleNodeClick(item)} style={{ cursor: 'pointer' }}>
                {title}
              </span>
            )}
            {item.level === 5 && (
              <Tag color="blue">{chapterNumber}</Tag>
            )}
            <Tag color={levelColors[item.level] || 'default'}>
              {levelLabels[item.level] || `层级${item.level}`}
            </Tag>
            <Space size="small" style={{ marginLeft: 'auto' }}>
              {item.level < 6 && (
                <Button
                  type="text"
                  icon={<PlusOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddChild(item);
                  }}
                  title="添加子项"
                />
              )}
              <Popconfirm
                title="确定要删除吗？"
                onConfirm={(e) => {
                  e.stopPropagation();
                  handleDelete(item);
                }}
                okText="确定"
                cancelText="取消"
              >
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={(e) => e.stopPropagation()}
                  title="删除"
                />
              </Popconfirm>
            </Space>
          </div>
        ),
        key: item.id,
        children: item.children ? renderTreeNodes(item.children) : []
      };
    });
  };

  // 获取所有节点的ID
  const getAllNodeIds = (nodes) => {
    let ids = [];
    nodes.forEach(node => {
      ids.push(node.id);
      if (node.children && node.children.length > 0) {
        ids = ids.concat(getAllNodeIds(node.children));
      }
    });
    return ids;
  };

  // 获取指定层级及以下的节点ID
  const getNodeIdsByLevel = (nodes, maxLevel) => {
    let ids = [];
    nodes.forEach(node => {
      if (node.level < maxLevel) {
        ids.push(node.id);
        if (node.children && node.children.length > 0) {
          ids = ids.concat(getNodeIdsByLevel(node.children, maxLevel));
        }
      }
    });
    return ids;
  };

  // 展开指定层级
  const expandToLevel = (level) => {
    const ids = getNodeIdsByLevel(treeData, level);
    setExpandedKeys(ids);
  };

  // 展开所有节点
  const expandAll = () => {
    const ids = getAllNodeIds(treeData);
    setExpandedKeys(ids);
  };

  // 折叠所有节点
  const collapseAll = () => {
    setExpandedKeys([]);
  };

  return (
    <div className="knowledge-management">
      <div className="header">
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            添加根节点
          </Button>
          <Divider type="vertical" />
          {Object.entries(expandLevels).map(([level, label]) => (
            <Button 
              key={level}
              onClick={() => expandToLevel(parseInt(level))}
              type="default"
            >
              展开到{label}
            </Button>
          ))}
          <Divider type="vertical" />
          <AntdInput
            placeholder="搜索节点"
            prefix={<SearchOutlined />}
            value={searchValue}
            onChange={(e) => searchNode(e.target.value)}
            style={{ width: 200 }}
          />
        </Space>
      </div>

      <div className="tree-container">
        <Tree
          showLine
          treeData={renderTreeNodes(treeData)}
          expandedKeys={expandedKeys}
          onExpand={setExpandedKeys}
          autoExpandParent={autoExpandParent}
        />
      </div>

      {isModalVisible && (
        <Modal
          title={modalTitle}
          open={isModalVisible}
          onOk={handleModalOk}
          onCancel={handleModalCancel}
          destroyOnClose
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={formValues}
          >
            <Form.Item
              name="name"
              label="名称"
              rules={[{ required: true, message: '请输入名称' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="level"
              hidden
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="parent_id"
              hidden
            >
              <Input />
            </Form.Item>
          </Form>
        </Modal>
      )}
    </div>
  );
};

export default KnowledgeManagement; 